import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Car, Bike, Navigation, Clock, DollarSign, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import RatingModal from "@/components/RatingModal";
import SponsoredAdWidget from "@/components/SponsoredAdWidget";
import RideChat from "@/components/RideChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const pickupIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="background:hsl(221 83% 53%);width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});
const destIcon = new L.DivIcon({
  className: "custom-marker",
  html: `<div style="background:hsl(142 71% 45%);width:24px;height:24px;border-radius:6px;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

function MapCenterer({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 14);
  }, [center, map]);
  return null;
}

async function geocode(query) {
  const res = await axios.get(`${API}/geocode`, { params: { q: query } });
  const data = res.data;
  if (!data || data.length === 0) return null;
  return { lat: data[0].lat, lng: data[0].lng, display: data[0].display };
}

function AddressAutocomplete({ value, onChange, onSelect, placeholder, testid, icon: Icon, iconColor }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const boxRef = useRef(null);
  const shouldFetchRef = useRef(false); // only fetch after user actually types / selects

  useEffect(() => {
    if (!shouldFetchRef.current) return; // skip mount + programmatic value writes
    if (!value || value.trim().length < 3) {
      setSuggestions([]); setOpen(false);
      return;
    }
    if (abortRef.current) clearTimeout(abortRef.current);
    abortRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/geocode`, { params: { q: value } });
        setSuggestions(res.data || []);
        setOpen(true);
      } catch (err) {
        console.debug("[Autocomplete] geocode error", err?.message);
        setSuggestions([]);
      }
      setLoading(false);
    }, 350);
    return () => abortRef.current && clearTimeout(abortRef.current);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      {Icon && <Icon className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${iconColor || "text-primary"}`} />}
      <Input
        data-testid={testid}
        className={Icon ? "pl-9 pr-9" : "pr-9"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          shouldFetchRef.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => value.trim().length >= 3 && suggestions.length > 0 && setOpen(true)}
        autoComplete="off"
      />
      {loading && (
        <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
      )}
      {open && suggestions.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg z-50"
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.map((s, idx) => (
            <button
              key={`${s.lat}-${s.lng}-${idx}`}
              type="button"
              data-testid={`${testid}-suggestion-${idx}`}
              onClick={() => {
                shouldFetchRef.current = false; // don't retrigger search from programmatic value write
                onSelect(s);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b border-border last:border-0 flex items-start gap-2"
            >
              <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <span className="text-sm line-clamp-2">{s.display}</span>
            </button>
          ))}
        </div>
      )}
      {open && !loading && suggestions.length === 0 && value.trim().length >= 3 && (
        <div className="absolute left-0 right-0 top-full mt-1 rounded-lg border border-border bg-popover shadow-lg z-50 p-3 text-sm text-muted-foreground" data-testid={`${testid}-no-results`}>
          Nenhum endereço encontrado
        </div>
      )}
    </div>
  );
}

export default function Mobility() {
  const { t } = useLang();
  const [pickup, setPickup] = useState({ lat: -23.5505, lng: -46.6333, address: "São Paulo, SP" });
  const [dest, setDest] = useState(null);
  const [pickupQ, setPickupQ] = useState("São Paulo, SP");
  const [destQ, setDestQ] = useState("");
  const [vehicle, setVehicle] = useState("car");
  const [estimate, setEstimate] = useState(null);
  const [ride, setRide] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [showRating, setShowRating] = useState(false);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const r = await axios.post(`${API}/coupons/validate`, { code: couponCode });
      setCouponApplied(r.data);
      toast.success(`Cupom aplicado: ${r.data.discount_pct}% off`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cupom inválido");
      setCouponApplied(null);
    }
  };

  useEffect(() => {
    // Try browser geolocation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setPickup({ lat: pos.coords.latitude, lng: pos.coords.longitude, address: "Minha localização" }),
        () => {}
      );
    }
  }, []);

  useEffect(() => {
    if (dest && pickup) {
      axios.post(`${API}/rides/estimate`, {
        pickup_lat: pickup.lat, pickup_lng: pickup.lng, pickup_address: pickup.address,
        dest_lat: dest.lat, dest_lng: dest.lng, dest_address: dest.address,
        vehicle_type: vehicle,
      }).then(res => setEstimate(res.data));
    }
  }, [dest, pickup, vehicle]);

  const requestRide = async () => {
    if (!dest) return toast.error("Escolha o destino");
    try {
      const res = await axios.post(`${API}/rides`, {
        pickup_lat: pickup.lat, pickup_lng: pickup.lng, pickup_address: pickup.address,
        dest_lat: dest.lat, dest_lng: dest.lng, dest_address: dest.address,
        vehicle_type: vehicle,
        coupon_code: couponApplied?.code || null,
      });
      setRide(res.data);
      toast.info(t("searchingDriver"));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao solicitar"); }
  };

  // Poll ride status waiting for driver acceptance AND for completion
  useEffect(() => {
    if (!ride || (ride.status !== "searching" && ride.status !== "accepted" && ride.status !== "in_progress")) return;
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/rides/${ride.ride_id}`);
        if (r.data.status !== ride.status) {
          setRide(r.data);
          if (r.data.status === "accepted") toast.success(t("rideAccepted"));
          if (r.data.status === "completed") {
            toast.success("Corrida finalizada!");
            setShowRating(true);
          }
        }
      } catch (err) {
        console.error("[Mobility] poll ride status failed", err);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [ride, t]);

  const cancelRide = async () => {
    if (!ride) return;
    try {
      await axios.post(`${API}/rides/${ride.ride_id}/cancel`);
      setRide(null);
      toast.info("Corrida cancelada");
    } catch { toast.error("Erro"); }
  };

  const payRide = async () => {
    try {
      const amount = ride.price;
      let lookup = "ride_10";
      if (amount <= 5) lookup = "ride_5";
      else if (amount <= 10) lookup = "ride_10";
      else if (amount <= 20) lookup = "ride_20";
      else lookup = "ride_50";
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        origin_url: window.location.origin,
        metadata: { ride_id: ride.ride_id, type: "ride" },
      });
      window.location.href = res.data.checkout_url;
    } catch { toast.error("Erro no pagamento"); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        <div className="space-y-4 order-2 lg:order-1">
          <SponsoredAdWidget variant="card" limit={1} />
          <Card className="p-5">
            <h2 className="text-2xl font-bold tracking-tight mb-4">{t("whereTo")}</h2>
            <div className="space-y-3">
              <AddressAutocomplete
                testid="pickup-input"
                value={pickupQ}
                onChange={setPickupQ}
                onSelect={(s) => {
                  setPickup({ lat: s.lat, lng: s.lng, address: s.display });
                  setPickupQ(s.display);
                }}
                placeholder={t("pickupLocation")}
                icon={MapPin}
                iconColor="text-primary"
              />
              <AddressAutocomplete
                testid="dest-input"
                value={destQ}
                onChange={setDestQ}
                onSelect={(s) => {
                  setDest({ lat: s.lat, lng: s.lng, address: s.display });
                  setDestQ(s.display);
                }}
                placeholder={t("destination")}
                icon={Navigation}
                iconColor="text-green-600"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              {[
                { key: "car", label: t("car"), icon: Car },
                { key: "moto", label: t("moto"), icon: Bike },
              ].map((v) => (
                <button
                  key={v.key}
                  data-testid={`vehicle-${v.key}`}
                  onClick={() => setVehicle(v.key)}
                  className={`p-4 rounded-xl border-2 transition-all ${vehicle === v.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                >
                  <v.icon className={`w-6 h-6 mx-auto mb-2 ${vehicle === v.key ? "text-primary" : "text-muted-foreground"}`} />
                  <div className="text-sm font-semibold">{v.label}</div>
                </button>
              ))}
            </div>

            {estimate && !ride && (
              <div className="mt-4 p-4 rounded-xl bg-muted/50 space-y-2 animate-pop-in">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("estimatedPrice")}</span>
                  <span className="font-bold text-lg">
                    {couponApplied ? (
                      <>
                        <span className="line-through text-muted-foreground text-sm mr-2">R$ {estimate.price.toFixed(2)}</span>
                        R$ {(estimate.price * (1 - couponApplied.discount_pct / 100)).toFixed(2)}
                      </>
                    ) : (
                      <>R$ {estimate.price.toFixed(2)}</>
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("eta")}</span>
                  <span className="font-semibold">{estimate.eta_min} {t("minutes")}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Distância</span>
                  <span className="font-semibold">{estimate.distance_km} km</span>
                </div>
                {/* Coupon */}
                <div className="pt-2 border-t border-border">
                  {!couponApplied ? (
                    <div className="flex gap-2">
                      <Input
                        data-testid="coupon-input"
                        placeholder="Cupom de desconto"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="h-9 text-sm"
                      />
                      <Button data-testid="apply-coupon-btn" onClick={applyCoupon} size="sm" variant="outline" className="rounded-full">Aplicar</Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-600 font-semibold">✓ {couponApplied.code} · -{couponApplied.discount_pct}%</span>
                      <button onClick={() => { setCouponApplied(null); setCouponCode(""); }} className="text-xs text-muted-foreground hover:text-destructive">
                        Remover
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!ride && (
              <Button data-testid="request-ride-btn" onClick={requestRide} disabled={!dest} className="w-full rounded-full h-12 mt-4" size="lg">
                {t("requestRide")}
              </Button>
            )}

            {ride && ride.status === "searching" && (
              <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-3 animate-pop-in">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-primary animate-spin" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-bold">{t("searchingDriver")}</div>
                    <div className="text-xs text-muted-foreground">Aguardando motoristas próximos...</div>
                  </div>
                </div>
                <Button data-testid="cancel-ride-btn" onClick={cancelRide} variant="outline" className="w-full rounded-full">
                  {t("cancelRide")}
                </Button>
              </div>
            )}

            {ride && (ride.status === "accepted" || ride.status === "in_progress") && ride.driver && (
              <div className="mt-4 p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-3 animate-pop-in">
                <div className="flex items-center gap-3">
                  <Avatar className="w-12 h-12 ring-2 ring-primary/40">
                    <AvatarImage src={ride.driver.picture} />
                    <AvatarFallback>{ride.driver.name[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="font-bold">{ride.driver.name}</div>
                    <div className="text-xs text-muted-foreground">⭐ {ride.driver.rating}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">{ride.eta_min} {t("minutes")}</div>
                    <div className="font-bold">R$ {ride.price.toFixed(2)}</div>
                  </div>
                </div>
                <div className="text-sm space-y-1 pt-2 border-t border-border">
                  <div><span className="text-muted-foreground">{t("vehicle")}: </span>{ride.driver.vehicle}</div>
                  <div><span className="text-muted-foreground">{t("plate")}: </span><span className="font-mono">{ride.driver.plate}</span></div>
                </div>
                <Button data-testid="pay-ride-btn" onClick={payRide} className="w-full rounded-full">
                  <DollarSign className="w-4 h-4 mr-1" /> {t("payNow")}
                </Button>
              </div>
            )}

            {/* Real-time chat with driver — during accepted/in_progress */}
            {ride && ride.ride_id && (ride.status === "accepted" || ride.status === "in_progress") && (
              <div className="mt-4">
                <RideChat
                  rideId={ride.ride_id}
                  rideStatus={ride.status}
                  otherName={ride.driver?.name || "Motorista"}
                />
              </div>
            )}
          </Card>
        </div>

        {/* Map */}
        <div className="order-1 lg:order-2 h-[400px] lg:h-[calc(100vh-140px)] rounded-2xl overflow-hidden border border-border">
          <MapContainer center={[pickup.lat, pickup.lng]} zoom={13} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapCenterer center={dest ? [(pickup.lat + dest.lat)/2, (pickup.lng + dest.lng)/2] : [pickup.lat, pickup.lng]} />
            <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon}>
              <Popup>Embarque</Popup>
            </Marker>
            {dest && (
              <Marker position={[dest.lat, dest.lng]} icon={destIcon}>
                <Popup>Destino</Popup>
              </Marker>
            )}
          </MapContainer>
        </div>
      </div>

      <RatingModal
        open={showRating}
        onOpenChange={(v) => { setShowRating(v); if (!v) setRide(null); }}
        ride={ride}
        target={ride?.driver ? { name: ride.driver.name, picture: ride.driver.picture, role: "Motorista" } : null}
      />
    </div>
  );
}
